import React, { useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, Clock, Receipt, ArrowCounterClockwise } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const METHOD_LABEL = { cash: ['Cash', 'Bar'], card: ['Card', 'Karte'], twint: ['TWINT', 'TWINT'], invoice: ['Invoice', 'Rechnung'] };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');

const StatusBadge = ({ row, L }) => {
  const map = {
    paid: ['bg-emerald-100 text-emerald-800', <CheckCircle key="i" size={13} weight="fill" />, L('Paid', 'Bezahlt')],
    invoice_open: ['bg-violet-100 text-violet-800', <Receipt key="i" size={13} />, L('Invoice open', 'Rechnung offen')],
    open: ['bg-amber-100 text-amber-800', <Clock key="i" size={13} />, L('Open', 'Offen')],
    legacy: ['bg-slate-100 text-slate-600', <CheckCircle key="i" size={13} />, L('Paid (no details)', 'Bezahlt (ohne Details)')],
  };
  const [cls, icon, label] = map[row.status] || map.open;
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid={`aa-status-${row.client_id}`}>{icon}{label}</span>;
};

const RecordForm = ({ row, L, onSave, onCancel, busy }) => {
  const [method, setMethod] = useState('cash');
  const [amount, setAmount] = useState(String(row.amount || ''));
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 bg-slate-50 rounded-lg p-2" data-testid={`aa-form-${row.client_id}`}>
      {Object.entries(METHOD_LABEL).map(([k, lbl]) => (
        <button key={k} type="button" onClick={() => setMethod(k)} data-testid={`aa-method-${k}-${row.client_id}`}
          className={`px-3 py-1 rounded-lg text-xs font-semibold border transition-colors ${method === k ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-slate-600 border-slate-200'}`}>
          {L(lbl[0], lbl[1])}
        </button>
      ))}
      <Input type="number" min="0" value={amount} onChange={e => setAmount(e.target.value)} className="h-8 w-24 bg-white text-right" data-testid={`aa-amount-${row.client_id}`} />
      <span className="text-xs text-slate-500">CHF</span>
      <Button size="sm" disabled={busy || !(parseFloat(amount) > 0)} onClick={() => onSave(method, parseFloat(amount))} className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`aa-save-${row.client_id}`}>
        {L('Save', 'Speichern')}
      </Button>
      <Button size="sm" variant="ghost" onClick={onCancel} className="h-8 text-slate-500">{L('Cancel', 'Abbrechen')}</Button>
    </div>
  );
};

const ParticipantRow = ({ row, course, API, L, onChange }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const p = row.payment;
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setOpen(false); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || L('Error', 'Fehler')); }
    finally { setBusy(false); }
  };
  const save = (method, amount) => run(() => API.post('/adult-academy/payments', { series_key: course.series_key, client_id: row.client_id, method, amount }), L('Payment recorded', 'Zahlung erfasst'));
  const invoicePaid = () => run(() => API.post(`/adult-academy/payments/${p.id}/invoice-paid`), L('Invoice marked paid', 'Rechnung als bezahlt markiert'));
  const undo = () => { if (window.confirm(L('Undo this payment (also removes the register entry)?', 'Zahlung rückgängig machen (Kassen-Eintrag wird ebenfalls gelöscht)?'))) run(() => API.delete(`/adult-academy/payments/${p.id}`), L('Payment removed', 'Zahlung entfernt')); };
  return (
    <div className="py-2.5 border-t border-slate-100 first:border-t-0" data-testid={`aa-row-${row.client_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 text-sm truncate">{row.name}</p>
          <p className="text-[11px] text-slate-500" data-testid={`aa-detail-${row.client_id}`}>
            {Number(row.amount || 0).toFixed(2)} CHF
            {p && ` · ${L(...METHOD_LABEL[p.method])}`}
            {p?.paid_at && ` · ${L('paid', 'bezahlt')} ${fmtDate(p.paid_at)}`}
            {p?.status === 'invoice_open' && ` · ${L('invoiced', 'verrechnet')} ${fmtDate(p.recorded_at)}`}
            {p && ` · ${L('by', 'erfasst von')} ${p.recorded_by_name}`}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <StatusBadge row={row} L={L} />
          {(row.status === 'open' || row.status === 'legacy') && !open && (
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} className="h-7 text-xs border-emerald-300 text-emerald-700" data-testid={`aa-record-${row.client_id}`}>{L('Record payment', 'Zahlung erfassen')}</Button>
          )}
          {row.status === 'invoice_open' && (
            <Button size="sm" onClick={invoicePaid} disabled={busy} className="h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white" data-testid={`aa-invoice-paid-${row.client_id}`}>{L('Invoice paid', 'Rechnung bezahlt')}</Button>
          )}
          {p && (
            <button type="button" onClick={undo} disabled={busy} title={L('Undo', 'Rückgängig')} className="p-1 text-slate-400 hover:text-rose-600" data-testid={`aa-undo-${row.client_id}`}><ArrowCounterClockwise size={15} /></button>
          )}
        </div>
      </div>
      {open && <RecordForm row={row} L={L} busy={busy} onSave={save} onCancel={() => setOpen(false)} />}
    </div>
  );
};

export const AdultAcademyCourseCard = ({ course, API, L, onChange, compact = false }) => (
  <div className={`bg-white border border-slate-200/70 rounded-2xl ${compact ? 'p-3' : 'p-5 shadow-sm'}`} data-testid={`aa-course-${course.series_key}`}>
    <div className="flex flex-wrap justify-between gap-2 mb-2">
      <div>
        <p className="font-bold text-slate-900">{course.weekday} {course.start_time}–{course.end_time} · {course.trainer_name}</p>
        <p className="text-xs text-slate-500">
          {course.court_name} · {fmtDate(course.first_date)} – {fmtDate(course.last_date)} · {course.sessions} {L('sessions', 'Termine')}
          {course.next_date && ` · ${L('next', 'nächster')} ${fmtDate(course.next_date)}`}
        </p>
      </div>
      <div className="text-right text-xs">
        <p className="font-semibold text-slate-700" data-testid={`aa-paid-count-${course.series_key}`}>{course.paid_count}/{course.participants.length} {L('paid', 'bezahlt')}</p>
        {course.open_total > 0 && <p className="text-amber-700 font-semibold">{L('Open', 'Offen')}: {course.open_total.toFixed(2)} CHF</p>}
      </div>
    </div>
    {course.participants.length === 0
      ? <p className="text-xs text-slate-400">{L('No participants', 'Keine Teilnehmer')}</p>
      : course.participants.map(r => <ParticipantRow key={r.client_id} row={r} course={course} API={API} L={L} onChange={onChange} />)}
  </div>
);
